/**
 * The sun's position and the day's sunrise, sunset and twilight times, to the
 * second (ERI-2396). SunCalc's times were up to 3.5 minutes off the Naval
 * Observatory, and its start of full darkness was up to 2 minutes late. This
 * is the Meeus (Astronomical Algorithms, ch. 25) solar position with each
 * time solved directly from the sun's altitude.
 *
 * Mirrored byte for byte in supabase/functions/_shared/sun-position.ts. No
 * imports, so the two copies stay identical (a test checks).
 */

const RAD = Math.PI / 180;
/** TT minus UTC, seconds (32.184 + 37 leap seconds since 2017). */
const TT_MINUS_UTC = 69.184;
const HOUR_MS = 3600 * 1000;

/** Geometric altitude and azimuth (from south, westward) of the sun's centre, in degrees. */
function sunAltAz(date: Date, latitude: number, longitude: number) {
  const jdUt = date.getTime() / 86400000 + 2440587.5;
  const T = (jdUt + TT_MINUS_UTC / 86400 - 2451545) / 36525;

  const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
  const M = (357.52911 + 35999.05029 * T - 0.0001537 * T * T) * RAD;
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(M)
    + (0.019993 - 0.000101 * T) * Math.sin(2 * M) + 0.000289 * Math.sin(3 * M);
  const omega = (125.04452 - 1934.136261 * T) * RAD;
  const Ls = (280.4665 + 36000.7698 * T) * RAD;
  const Lm = (218.3165 + 481267.8813 * T) * RAD;
  const dPsi = (-17.2 * Math.sin(omega) - 1.32 * Math.sin(2 * Ls) - 0.23 * Math.sin(2 * Lm) + 0.21 * Math.sin(2 * omega)) / 3600;
  const dEps = (9.2 * Math.cos(omega) + 0.57 * Math.cos(2 * Ls) + 0.1 * Math.cos(2 * Lm) - 0.09 * Math.cos(2 * omega)) / 3600;
  // Apparent longitude: true longitude, aberration (-20.4"), nutation.
  const lambda = (L0 + C - 0.005691611 + dPsi) * RAD;
  const eps = (23.43929111 - (46.815 * T) / 3600 + dEps) * RAD;

  const ra = Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda));
  const dec = Math.asin(Math.sin(eps) * Math.sin(lambda));

  const d = jdUt - 2451545;
  const gmst = 18.697374558 + 24.06570982441908 * d;
  const theta = ((((gmst + longitude / 15) % 24) + 24) % 24) * 15 * RAD + dPsi * Math.cos(eps) * RAD;
  const H = theta - ra;
  const phi = latitude * RAD;
  const alt = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H));
  const az = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  // Parallax: the sun is 8.8" lower from the Earth's surface than from its centre.
  const altDeg = alt / RAD - (8.794 / 3600) * Math.cos(alt);
  return { altitude: altDeg, azimuth: az / RAD, hourAngle: Math.atan2(Math.sin(H), Math.cos(H)) / RAD };
}

/** The sun's geometric (airless) altitude in degrees and azimuth in degrees from south. */
export function sunPosition(date: Date, latitude: number, longitude: number): { altitude: number; azimuth: number } {
  const p = sunAltAz(date, latitude, longitude);
  return { altitude: p.altitude, azimuth: p.azimuth };
}

/** Same keys as SunCalc.getTimes. A time that doesn't happen that day is an Invalid Date, as in SunCalc. */
export interface SunTimes {
  solarNoon: Date;
  nadir: Date;
  sunrise: Date;
  sunset: Date;
  sunriseEnd: Date;
  sunsetStart: Date;
  dawn: Date;
  dusk: Date;
  nauticalDawn: Date;
  nauticalDusk: Date;
  nightEnd: Date;
  night: Date;
  goldenHourEnd: Date;
  goldenHour: Date;
}

// Centre altitudes (degrees, geometric) for each pair of times. Sunrise and
// sunset are the upper limb on the horizon with the standard 34' of refraction.
const LEVELS: [number, keyof SunTimes, keyof SunTimes][] = [
  [-0.8333, 'sunrise', 'sunset'],
  [-0.3, 'sunriseEnd', 'sunsetStart'],
  [-6, 'dawn', 'dusk'],
  [-12, 'nauticalDawn', 'nauticalDusk'],
  [-18, 'nightEnd', 'night'],
  [6, 'goldenHourEnd', 'goldenHour'],
];

/** The instant between a and b (ms) where the altitude crosses `level`, to half a second. */
function crossing(a: number, b: number, level: number, latitude: number, longitude: number): Date {
  const f = (t: number) => sunAltAz(new Date(t), latitude, longitude).altitude - level;
  let fa = f(a);
  const fb = f(b);
  if ((fa < 0) === (fb < 0)) return new Date(NaN);
  while (b - a > 500) {
    const mid = (a + b) / 2;
    const fm = f(mid);
    if ((fm < 0) === (fa < 0)) {
      a = mid;
      fa = fm;
    } else {
      b = mid;
    }
  }
  return new Date(Math.round((a + b) / 2));
}

/**
 * Sunrise, sunset and twilight for the solar day whose noon is nearest `date`,
 * the same day SunCalc.getTimes picks (callers pass noon UTC on the date).
 */
export function getSunTimes(date: Date, latitude: number, longitude: number): SunTimes {
  // Solar noon: the hour angle reaches zero. Three steps converge to well under a second.
  let noon = date.getTime();
  for (let i = 0; i < 3; i++) noon -= (sunAltAz(new Date(noon), latitude, longitude).hourAngle / 360) * 24 * HOUR_MS * 0.99727;
  const before = noon - 12 * HOUR_MS;
  const after = noon + 12 * HOUR_MS;
  const out = { solarNoon: new Date(Math.round(noon)), nadir: new Date(Math.round(before)) } as SunTimes;
  for (const [level, morning, evening] of LEVELS) {
    out[morning] = crossing(before, noon, level, latitude, longitude);
    out[evening] = crossing(noon, after, level, latitude, longitude);
  }
  return out;
}
